import type { Feature } from 'geojson'
import L from 'leaflet'
import { useMemo } from 'react'
import { GeoJSON } from 'react-leaflet'
import { useRefData } from '../../hooks/data/useRefData'
import type { RefData } from '../../types/apiModels'
import { escapeHtml } from '../../utils/escapeHtml'

export type QUOverlayItem = {
  id: string
  name: string | null
  status: 'pending_improvement' | 'active' | 'archived'
  totalHectares: number
  baselineYear: string
  improvementYear: number
  baselineUsage?: number | null
  toGeoJSON?: Feature | null
}

const STATUS_STYLES: Record<string, L.PathOptions> = {
  pending_improvement: { fillColor: '#9e9e9e', color: '#616161' },
  active: { fillColor: '#4caf50', color: '#2e7d32' },
  archived: { fillColor: '#ff9800', color: '#e65100' },
}

const BASE_STYLE: L.PathOptions = {
  weight: 2,
  opacity: 0.8,
  fillOpacity: 0.5,
  dashArray: '5, 5',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Activa',
  pending_improvement: 'Pendiente de mejora',
  archived: 'Archivada',
}

type QUOverlayProps = {
  quantificationUnits: QUOverlayItem[]
}

type UsageLabelById = Record<string | number, string>

const HASH_MODULUS = 2147483647

export const buildUsageLabelById = (usages: RefData[] | undefined): UsageLabelById =>
  (usages ?? []).reduce<UsageLabelById>((acc, usage) => {
    const label = usage.es_AR || usage.en_US
    if (label) acc[usage.id] = label
    return acc
  }, {})

export const buildOverlayKey = (quantificationUnits: QUOverlayItem[]): string => {
  const hash = quantificationUnits.reduce((outer, qu) => {
    // Coordinates, not totalHectares: a reshaped QU can keep its area, and react-leaflet only redraws on a key change.
    const geometry = qu.toGeoJSON?.geometry
    const geometrySignature = JSON.stringify(
      geometry && 'coordinates' in geometry ? geometry.coordinates : null,
    )
    const fragment = `${qu.id}-${qu.status}-${geometrySignature}`
    let inner = outer
    for (let index = 0; index < fragment.length; index += 1) {
      inner = (inner * 31 + fragment.charCodeAt(index)) % HASH_MODULUS
    }
    return inner
  }, 0)
  return `qu-overlay-${quantificationUnits.length}-${hash}`
}

export const buildFeatureCollection = (quantificationUnits: QUOverlayItem[]) => {
  const features = quantificationUnits
    .filter((qu): qu is QUOverlayItem & { toGeoJSON: Feature } => !!qu.toGeoJSON?.geometry)
    .map(qu => ({
      ...qu.toGeoJSON,
      properties: {
        ...(qu.toGeoJSON.properties || {}),
        quId: qu.id,
        quName: qu.name,
        quStatus: qu.status,
        totalHectares: qu.totalHectares,
        baselineYear: qu.baselineYear,
        improvementYear: qu.improvementYear,
        color: qu.toGeoJSON.properties?.color,
        baselineUsage: qu.baselineUsage ?? qu.toGeoJSON.properties?.baselineUsage,
      },
    }))

  return {
    type: 'FeatureCollection' as const,
    features,
  }
}

export const createFeatureHandler =
  (usageLabelById: UsageLabelById) => (feature: Feature, layer: L.Layer) => {
    const props = feature?.properties
    if (!props) return

    const status = props.quStatus || 'pending_improvement'
    const statusColors = STATUS_STYLES[status] || STATUS_STYLES.pending_improvement
    const resolvedColors = props.color
      ? { fillColor: props.color, color: props.color }
      : statusColors
    ;(layer as L.Path).setStyle({ ...BASE_STYLE, ...resolvedColors })

    const statusLabel = STATUS_LABELS[status] || status
    const hectares = props.totalHectares?.toFixed(1) ?? 'N/A'
    const usageLabel = props.baselineUsage != null ? usageLabelById[props.baselineUsage] : undefined
    const namePrefix = props.quName ? `${escapeHtml(props.quName)} ` : 'QU '
    const secondLineParts =
      props.improvementYear != null ? [`Inicio proyecto ${escapeHtml(props.improvementYear)}`] : []
    if (usageLabel) secondLineParts.push(`Uso de base: ${escapeHtml(usageLabel)}`)
    const tooltip = [
      `<div style="font-weight:600">${namePrefix}${escapeHtml(statusLabel)} · ${escapeHtml(hectares)} ha</div>`,
      `<div style="opacity:0.85">${secondLineParts.join(' · ')}</div>`,
    ].join('')
    layer.bindTooltip(tooltip)
  }

export const handleEachFeature = createFeatureHandler({})

const QUOverlay = ({ quantificationUnits }: QUOverlayProps) => {
  const { data: reterraUsages } = useRefData('RefRETERRAUsage')

  const usageLabelById = useMemo(() => buildUsageLabelById(reterraUsages), [reterraUsages])

  const onEachFeature = useMemo(() => createFeatureHandler(usageLabelById), [usageLabelById])
  const featureCollection = useMemo(
    () => buildFeatureCollection(quantificationUnits),
    [quantificationUnits],
  )
  const overlayKey = useMemo(
    () => `${buildOverlayKey(quantificationUnits)}-${Object.keys(usageLabelById).length}`,
    [quantificationUnits, usageLabelById],
  )

  if (!quantificationUnits?.length) return null

  return (
    <GeoJSON
      key={overlayKey}
      data={featureCollection}
      style={BASE_STYLE}
      onEachFeature={onEachFeature}
    />
  )
}

export default QUOverlay
